import { Fragment, useState } from "react";
import { FileText, Mail, Pencil, Plus, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, PageHeader, Pager, Panel, RowsSkeleton, SearchBox, Tag } from "@/components/app/kit";
import { FormDialog } from "@/components/app/FormDialog";
import { useFetch, useServerList } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/i18n";

const Templates = ({ isSuper }) => {
  const { data, loading, reload } = useFetch("/platform/templates");
  const [edit, setEdit] = useState(null);
  const fields = [
    ...(edit?.isNew ? [{ key: "code", label: "Code", required: true, mono: true }] : []),
    { key: "name", label: "Nom", required: true },
    { key: "type", label: "Type", type: "select", required: true, options: [["notification", "Notification"], ["document", "Modèle de document"]] },
    { key: "channel", label: "Canal", type: "select", required: true, options: [["email", "E-mail"], ["sms", "SMS"], ["pdf", "Document PDF"]] },
    { key: "subject", label: "Objet / titre", full: true },
    { key: "body", label: "Contenu", type: "textarea", required: true, rows: 8, hint: "Variables : {{nom}}, {{lien}}, {{cabinet}}…" },
  ];
  return (
    <Panel actions={isSuper && <Button size="sm" onClick={() => setEdit({ isNew: true, type: "notification", channel: "email" })} data-testid="templates-create-button"><Plus className="h-4 w-4 mr-2" />Nouveau modèle</Button>} title="Modèles">
      {loading ? <RowsSkeleton /> : !data?.length ? <EmptyState icon={FileText} title="Aucun modèle" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {data.map((tpl) => (
            <div key={tpl.code} className="rounded-xl border border-slate-200 p-4 min-w-0" data-testid={`template-card-${tpl.code}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm break-words">{tpl.name}</p>
                  <p className="font-mono text-[11px] text-slate-400 break-all">{tpl.code}</p>
                </div>
                <div className="flex gap-1.5 items-center">
                  <Tag tone={tpl.type === "document" ? "indigo" : "sky"}>{tpl.type === "document" ? "Document" : "Notification"}</Tag>
                  {isSuper && <Button variant="ghost" size="sm" onClick={() => setEdit(tpl)} data-testid={`template-edit-${tpl.code}`}><Pencil className="h-4 w-4" /></Button>}
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-3 font-medium">{tpl.subject}</p>
              <p className="text-xs text-slate-500 mt-1 line-clamp-3 whitespace-pre-line">{tpl.body}</p>
            </div>
          ))}
        </div>
      )}
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} wide testid="template-dialog" title={edit?.isNew ? "Nouveau modèle" : `Modifier « ${edit?.name} »`}
        initial={edit || {}} fields={fields}
        onSubmit={async (v) => {
          const body = { name: v.name, type: v.type, channel: v.channel, subject: v.subject || "", body: v.body, variables: [...new Set((v.body.match(/{{(\w+)}}/g) || []).map((x) => x.slice(2, -2)))] };
          if (edit.isNew) await api.post("/platform/templates", { code: v.code, ...body });
          else await api.put(`/platform/templates/${edit.code}`, body);
          toast.success("Modèle enregistré");
          reload();
        }} />
    </Panel>
  );
};

const Outbox = ({ isSuper }) => {
  const list = useServerList("/platform/outbox");
  const [open, setOpen] = useState(null);
  const [revealed, setRevealed] = useState({});
  const reveal = async (id) => {
    try { const r = await api.get(`/platform/outbox/${id}/reveal`); setRevealed((s) => ({ ...s, [id]: r.data.body })); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Panel title="Journal d'envoi" className="overflow-hidden">
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
        Aucun fournisseur d'e-mail n'est configuré : les messages sont générés et conservés ici avec le statut « non envoyé ».
      </p>
      <div className="mb-4"><SearchBox value={list.filters.search} onChange={(v) => list.setFilter("search", v)} placeholder="Destinataire, objet…" testid="outbox-search" /></div>
      <div className="-mx-5 overflow-x-auto">
        {list.loading ? <RowsSkeleton /> : list.items.length === 0 ? <EmptyState icon={Inbox} title="Aucun message" /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Destinataire</TableHead><TableHead>Objet</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
            <TableBody>
              {list.items.map((m) => (
                <Fragment key={m.id}>
                  <TableRow className="cursor-pointer" onClick={() => setOpen(open === m.id ? null : m.id)} data-testid="outbox-row">
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(m.created_at)}</TableCell>
                    <TableCell className="text-sm">{m.to}</TableCell>
                    <TableCell className="text-sm">{m.subject}</TableCell>
                    <TableCell><Tag tone="amber">Non envoyé</Tag></TableCell>
                  </TableRow>
                  {open === m.id && <TableRow><TableCell colSpan={4} className="bg-slate-50"><pre className="text-xs whitespace-pre-wrap break-all font-mono text-slate-600" data-testid="outbox-body">{revealed[m.id] || m.body}</pre>
                    {isSuper && !revealed[m.id] && m.body.includes("[jeton masqué]") && <Button size="sm" variant="outline" className="mt-2" onClick={() => reveal(m.id)} data-testid="outbox-reveal">Afficher le lien complet (action journalisée)</Button>}</TableCell></TableRow>}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <div className="-mx-5 -mb-5"><Pager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} /></div>
    </Panel>
  );
};

export default function Notifications() {
  const { me } = useAuth();
  return (
    <div>
      <PageHeader eyebrow="Communication" title="Notifications et modèles de documents" subtitle="Modèles d'e-mails transactionnels, de documents médicaux et historique des messages générés." />
      <Tabs defaultValue="templates">
        <TabsList className="mb-6">
          <TabsTrigger value="templates" data-testid="notifications-tab-templates"><Mail className="h-4 w-4 mr-2" />Modèles</TabsTrigger>
          <TabsTrigger value="outbox" data-testid="notifications-tab-outbox"><Inbox className="h-4 w-4 mr-2" />Journal d'envoi</TabsTrigger>
        </TabsList>
        <TabsContent value="templates"><Templates isSuper={me.user.platform_role === "super_admin"} /></TabsContent>
        <TabsContent value="outbox"><Outbox isSuper={me.user.platform_role === "super_admin"} /></TabsContent>
      </Tabs>
    </div>
  );
}
